import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Leaf, Menu, X, LayoutDashboard, LogOut } from "lucide-react";

const NAV_LINKS = [
  { to: "/search", label: "Find Providers" },
  { to: "/compare", label: "Compare" },
  { to: "/pricing", label: "For Pros" },
  { to: "/affiliate", label: "Affiliate" },
  { to: "/blog", label: "Learn" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const dash = user
    ? user.role === "admin" ? "/admin" : user.role === "provider" ? "/provider" : "/dashboard"
    : "/login";

  return (
    <header className="glass-header sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-lawn flex items-center justify-center">
            <Leaf className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <span className="font-display text-xl font-extrabold text-lawn-ink">LawnLink<span className="text-lawn">Pro</span></span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV_LINKS.map((l) => (
            <Link key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className="text-sm font-semibold text-lawn-ink/70 hover:text-lawn transition-colors">
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          {user ? (
            <>
              <Button variant="ghost" data-testid="nav-dashboard-btn" onClick={() => navigate(dash)} className="gap-2 font-semibold">
                <LayoutDashboard className="w-4 h-4" /> Dashboard
              </Button>
              <Button variant="outline" data-testid="nav-logout-btn" onClick={async () => { await logout(); navigate("/"); }} className="gap-2 rounded-full">
                <LogOut className="w-4 h-4" /> Logout
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" data-testid="nav-login-btn" onClick={() => navigate("/login")} className="font-semibold">Log in</Button>
              <Button data-testid="nav-signup-btn" onClick={() => navigate("/signup")} className="rounded-full bg-lawn hover:bg-lawn-dark font-semibold">Get Started</Button>
            </>
          )}
        </div>

        <button data-testid="nav-mobile-toggle" className="lg:hidden p-2" onClick={() => setOpen(!open)}>
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-border bg-white px-5 py-4 space-y-3">
          {NAV_LINKS.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="block text-sm font-semibold text-lawn-ink/80 py-1.5">{l.label}</Link>
          ))}
          <div className="pt-2 flex flex-col gap-2">
            {user ? (
              <>
                <Button onClick={() => { setOpen(false); navigate(dash); }} className="w-full bg-lawn hover:bg-lawn-dark">Dashboard</Button>
                <Button variant="outline" onClick={async () => { setOpen(false); await logout(); navigate("/"); }} className="w-full">Logout</Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => { setOpen(false); navigate("/login"); }} className="w-full">Log in</Button>
                <Button onClick={() => { setOpen(false); navigate("/signup"); }} className="w-full bg-lawn hover:bg-lawn-dark">Get Started</Button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
